/**
 * 操控变形会话控制器（Puppet Warp 计划）。
 *
 * 会话模型对齐 `liquify-tool.ts`：菜单进入会话 → 交互式放置/拖动图钉 →
 * 确定提交（wasm 烘焙，一条历史）→ 取消丢弃（什么都不发生）。不经过
 * `ToolManager`——同液化，是独立会话不是常驻工具。
 *
 * 图钉的新增/选中/拖拽手势判定交给 UI 层（`PuppetWarpDialog.tsx`）：
 * down 时先 `hitTestPin`，命中则记住 id 后续 `movePin`，未命中才 `addPin`——
 * 控制器本身不维护"正在拖拽哪个"的状态，本来就只需要 UI 记一个变量。
 *
 * 预览走代理分辨率（长边降采样到 `proxyLongEdge`），提交才用图层实际内容
 * 全分辨率——MLS 是全局支持（每个像素都被每个图钉影响，没有液化那种
 * "只算笔刷附近"的捷径），对整张大图逐像素跑一遍明显扛不住实时拖拽帧率，
 * 这是 DESIGN.md §5.4"拖动用代理、松手算全分辨率"原则第一次真正落地。
 */
import { resample_lanczos3_rgba8 } from '../../wasm/core/photosmith_core';
import { ensureKernel } from '../../engine/wasm-kernel';
import type { LayerId } from '../../engine/tile/key';
import type { Point, Rect } from '../../engine/liquify/field';
import { warpRegion } from '../../engine/liquify/warp';
import { flattenLayerRegion } from '../../engine/liquify/bake';
import { computeMlsField } from '../../engine/puppet/mls';
import { bakePuppetWarp, contentBounds } from '../../engine/puppet/bake';
import type { AppStore } from '../../app/store';

export interface Pin {
  readonly id: number;
  /** 放置时的原始位置，不随拖拽改变。 */
  readonly origin: Point;
  /** 拖动后的当前位置；未拖动时等于 `origin`。 */
  current: Point;
}

export interface PuppetPreviewFrame {
  /** 预览帧相对文档坐标的缩放比例（代理边长/内容边长）。 */
  readonly scale: number;
  /** 内容外接矩形的左上角在文档坐标里的位置——UI 换算图钉点击坐标要用
   * （`docPoint = origin + proxyPoint / scale`），预览帧本身不从 (0,0) 开始
   * 画是因为图层内容不一定贴着文档原点。 */
  readonly origin: Point;
  /** 代理空间下的内容外接矩形（左上角为 (0,0)，宽高已按 scale 缩放）。 */
  readonly rect: Rect;
  /** RGBA8，长度 rect.width*rect.height*4。 */
  readonly data: Uint8Array;
}

interface Session {
  readonly layer: LayerId;
  readonly pins: Pin[];
  nextId: number;
}

export interface PuppetWarpDeps {
  readonly store: AppStore;
  readonly requestRender: () => void;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export class PuppetWarpController {
  readonly #deps: PuppetWarpDeps;
  #session: Session | null = null;

  constructor(deps: PuppetWarpDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#session !== null;
  }

  get pins(): readonly Pin[] {
    return this.#session?.pins ?? [];
  }

  /**
   * 对当前活动 raster 图层开始会话。没有活动图层、活动图层不是 raster、
   * 或图层没有任何已分配像素块时返回 `false`（同液化）。
   */
  begin(): boolean {
    if (this.#session !== null) return true;
    const store = this.#deps.store;
    const document = store.document;
    if (document === null) return false;
    const layer = store.activeRasterLayerId();
    if (layer === null) return false;
    if (store.tiles.residentTilesOf(layer, 0).next().done === true) return false;

    this.#session = { layer, pins: [], nextId: 1 };
    this.#deps.requestRender();
    return true;
  }

  cancel(): void {
    if (this.#session === null) return;
    this.#session = null;
    this.#deps.requestRender();
  }

  /** 新增一个图钉（origin=current=point），返回它的 id。 */
  addPin(point: Point): number {
    const session = this.#session;
    if (session === null) throw new Error('没有激活的操控变形会话');
    const id = session.nextId++;
    session.pins.push({ id, origin: point, current: point });
    return id;
  }

  /** 命中测试（圆形容差，文档像素）。未命中返回 `null`。 */
  hitTestPin(point: Point, toleranceDoc: number): number | null {
    const session = this.#session;
    if (session === null) return null;
    // 后添加的图钉视觉上在上层，命中测试从后往前找
    for (let i = session.pins.length - 1; i >= 0; i--) {
      const pin = session.pins[i]!;
      if (distance(pin.current, point) <= toleranceDoc) return pin.id;
    }
    return null;
  }

  movePin(id: number, point: Point): void {
    const pin = this.#session?.pins.find((p) => p.id === id);
    if (pin === undefined) return;
    pin.current = point;
  }

  removePin(id: number): void {
    const session = this.#session;
    if (session === null) return;
    const index = session.pins.findIndex((p) => p.id === id);
    if (index >= 0) session.pins.splice(index, 1);
  }

  /**
   * 按当前图钉状态渲染一帧代理分辨率预览（长边降采样到
   * `proxyLongEdge`）。图层无内容或会话未激活返回 `null`。
   */
  async previewFrame(proxyLongEdge: number): Promise<PuppetPreviewFrame | null> {
    const session = this.#session;
    const document = this.#deps.store.document;
    if (session === null || document === null) return null;

    const bounds = contentBounds(this.#deps.store.tiles, session.layer, document.size.width, document.size.height);
    if (bounds.width <= 0 || bounds.height <= 0) return null;

    await ensureKernel();
    const fullRes = flattenLayerRegion(this.#deps.store.tiles, session.layer, bounds);
    const longEdge = Math.max(bounds.width, bounds.height);
    const scale = Math.min(1, proxyLongEdge / longEdge);
    const proxyW = Math.max(1, Math.round(bounds.width * scale));
    const proxyH = Math.max(1, Math.round(bounds.height * scale));
    const proxySource =
      scale === 1 ? fullRes : resample_lanczos3_rgba8(fullRes, bounds.width, bounds.height, proxyW, proxyH);

    const proxyPins = session.pins.map((pin) => ({
      current: { x: (pin.current.x - bounds.x) * scale, y: (pin.current.y - bounds.y) * scale },
      origin: { x: (pin.origin.x - bounds.x) * scale, y: (pin.origin.y - bounds.y) * scale },
    }));
    const proxyRect: Rect = { x: 0, y: 0, width: proxyW, height: proxyH };
    const { dx, dy } = await computeMlsField(proxyPins, proxyRect);
    const data = await warpRegion(proxySource, proxyW, proxyH, dx, dy);
    return { scale, origin: { x: bounds.x, y: bounds.y }, rect: proxyRect, data };
  }

  /**
   * 提交：没有任何图钉被拖动过（等价空场）等价取消，不产生历史。否则
   * 对图层实际内容全分辨率跑一次 MLS + warp，一次性烘焙，一条历史。
   */
  async commit(options: { signal?: AbortSignal } = {}): Promise<boolean> {
    const session = this.#session;
    const store = this.#deps.store;
    const document = store.document;
    if (session === null || document === null) return false;

    const label = '操控变形';
    const transaction = store.beginPixelTransaction(label);
    const result = await bakePuppetWarp(
      store.tiles,
      session.layer,
      session.pins,
      document.size.width,
      document.size.height,
      transaction,
      options,
    );
    store.commitPixels(label, transaction.end());
    if (result !== null) store.invalidatePixels(result.dirty);

    this.#session = null;
    this.#deps.requestRender();
    return true;
  }
}
